import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconCheck } from '@tabler/icons-react';
import doctorApi from '../../api/doctor/doctorApi';
import WeekStrip from '../../components/booking/WeekStrip';
import Segmented from '../../components/common/Segmented';
import { DAY_PERIODS } from '../../utils/constants';
import {
  addDays, formatDayDate, formatTime, fromDateKey, monthLong, startOfWeek, timeToMinutes, toDateKey,
} from '../../utils/date';
import { PATHS, toPath } from '../../routes/paths';

/**
 * Live mini booking widget in the hero: shows real availability for the first featured doctor.
 * Picking a slot hands off to the booking screen (login first if needed).
 */
export default function HeroBookingPreview({ doctor }) {
  const navigate = useNavigate();
  const [week, setWeek] = useState([]);
  const [selected, setSelected] = useState(null);
  const [slots, setSlots] = useState([]);
  const [period, setPeriod] = useState('morning');

  useEffect(() => {
    if (!doctor?.nextAvailable) return;
    const start = startOfWeek(fromDateKey(doctor.nextAvailable.date));
    // Show the week after the earliest slot so the preview has plenty of open times.
    const weekStart = addDays(start, 7);
    const from = toDateKey(weekStart);
    const to = toDateKey(addDays(weekStart, 6));
    doctorApi.availability(doctor.id, { from, to }).then((list) => {
      const days = list.map((d) => ({ date: fromDateKey(d.date), available: d.slotsOpen > 0 }));
      setWeek(days);
      const first = list.find((d) => d.slotsOpen > 0);
      if (first) setSelected(first.date);
    });
  }, [doctor]);

  useEffect(() => {
    if (!selected || !doctor) return;
    doctorApi.slots(doctor.id, selected).then(setSlots);
  }, [selected, doctor]);

  const visible = useMemo(() => {
    const p = DAY_PERIODS.find((x) => x.key === period);
    return slots.filter((s) => timeToMinutes(s.time) >= timeToMinutes(p.from) && timeToMinutes(s.time) < timeToMinutes(p.to));
  }, [slots, period]);

  if (!doctor || !week.length) return <div className="skeleton hero-preview hero-preview--loading" />;

  const goBook = (time) => navigate(toPath(PATHS.book, { doctorId: doctor.id }), { state: { date: selected, time } });

  return (
    <div className="hero-preview">
      <div className="hero-preview__head">
        <span className="hero-preview__month">{`${monthLong(week[0].date).toUpperCase()} ${week[0].date.getFullYear()}`}</span>
        {selected && <span className="hero-preview__selected">{formatDayDate(selected)}</span>}
      </div>
      <WeekStrip days={week} selected={selected} onSelect={setSelected} variant="plain" />
      <div className="hero-preview__divider" />
      <Segmented items={DAY_PERIODS.map((p) => ({ key: p.key, label: p.label }))} value={period} onChange={setPeriod} />
      <div className="slot-grid slot-grid--compact">
        {visible.slice(0, 6).map((s) => (
          <button key={s.time} type="button" className="slot" disabled={!s.available} onClick={() => goBook(s.time)}>
            {formatTime(s.time)}
          </button>
        ))}
      </div>
      <div className="hero-preview__foot">
        <span className="hero-preview__check"><IconCheck size={16} stroke={2.4} /></span>
        Booked in under a minute
      </div>
    </div>
  );
}
